import {
  BusinessInfo,
  CatalogServiceDto,
  ServiceType,
} from '@wix/bookings-uou-types/dist/src';
import {
  DurationMapper,
  DurationMapperOptions,
  PaymentDtoMapper,
} from '@wix/bookings-uou-mappers';
import { LocationMapper } from '../../mappers/location.mapper';
import { BookingPolicyDto } from '../../types/shared-types';

export interface DetailsSectionViewModel {
  duration?: string;
  durationAria?: string;
  price?: string;
  location?: string;
  locations?: string[];
  isBookable: boolean;
  ariaPrice?: string;
  numberOfSpotsLeft?: string;
  isFullyBooked?: boolean;
}

export enum CourseAvailabilityDisplay {
  NUMBER_OF_SPOTS = 'NUMBER_OF_SPOTS',
  AVAILABILITY = 'AVAILABILITY',
}

function getNumberOfSpots(spotsLeft: number, settings, t: Function) {
  let numberOfSpotsLeft = '';
  if (settings.displayNumberOfSpots) {
    const customAvailabilityMessageText: string =
      settings.numberOfSpotsLeftMessageText &&
      `${spotsLeft} ${settings.numberOfSpotsLeftMessageText}`;
    numberOfSpotsLeft =
      customAvailabilityMessageText ||
      t('service.course.number-of-spots-left', {
        count: spotsLeft,
      });
  } else {
    numberOfSpotsLeft =
      settings.availabilityMessageText || t('service.course.available-spots');
  }
  return numberOfSpotsLeft;
}

export const detailsSectionViewModelFactory = ({
  t,
  businessInfo,
  serviceDto,
  isBookable,
  viewTimezone,
  bookingPolicyDto,
  settings = {},
}: {
  t: Function;
  businessInfo: BusinessInfo;
  serviceDto: CatalogServiceDto;
  isBookable: boolean;
  viewTimezone?: string;
  bookingPolicyDto: BookingPolicyDto;
  settings?: any;
}): DetailsSectionViewModel => {
  const regionalSettingsLocale =
    businessInfo?.regionalSettingsLocale ?? 'en-US';
  const dateRegionalSettingsLocale =
    businessInfo?.dateRegionalSettingsLocale ?? 'en-US';
  const serviceType = serviceDto?.type;
  const isDurationVaries = serviceDto?.scheduleHeader?.isDurationVaries;
  const startDateAsUTC = serviceDto?.scheduleHeader?.startDateAsUTC;
  const durationInMinutes = serviceDto?.scheduleHeader?.durationInMinutes;
  const isPassedEndDate = serviceDto.schedulePolicy?.isPassedEndDate;

  const durationOptions: DurationMapperOptions = {
    hourUnit: 'duration.units.hours',
    hourAriaUnit: 'duration.units.aria-hours',
    minuteUnit: 'duration.units.minutes',
    minuteAriaUnit: 'duration.units.aria-minutes',
    durationVaries: 'duration.varies',
    coursePassedText: 'service.course.schedule.start-date-passed',
    courseInFutureText: 'service.course.schedule.start-date-in-future',
    courseEndedText: 'service.course.schedule.ended',
  };
  const durationMapper = new DurationMapper(
    dateRegionalSettingsLocale,
    durationOptions,
    t,
  );
  const duration = durationMapper.durationText({
    serviceType,
    isDurationVaries,
    startDateAsUTC,
    durationInMinutes,
    viewTimezone,
    isPassedEndDate,
  });

  const durationAria = durationMapper.durationText({
    serviceType,
    isDurationVaries,
    startDateAsUTC,
    durationInMinutes,
    durationAria: true,
    viewTimezone,
    isPassedEndDate,
  });

  const paymentDtoMapper = new PaymentDtoMapper(
    regionalSettingsLocale,
    (price) =>
      serviceDto.payment.isVariedPricing
        ? t('service.payment.from-price', { price })
        : price,
  );
  const price = paymentDtoMapper.priceText(serviceDto?.payment);
  const ariaPrice = paymentDtoMapper.priceText(serviceDto?.payment, 'name');

  const clientLocationText = t('service.location.customer-place');
  const locationMapper = new LocationMapper(clientLocationText);
  const location = locationMapper.text({
    serviceLocation: serviceDto.info?.location,
  });

  const serviceDtoLocations = serviceDto.info?.locations;
  const useBusinessName = !!serviceDtoLocations?.length;
  const locations = serviceDtoLocations
    ?.map((serviceLocation) =>
      locationMapper.text({
        serviceLocation,
        useBusinessName,
      }),
    )
    .filter((serviceLocation) => !!serviceLocation);

  const spots = bookingPolicyDto.numberOfSpotsLeft!;
  const showAvailability =
    serviceType === ServiceType.COURSE &&
    spots > 0 &&
    !bookingPolicyDto.isTooLateToBook;
  const numberOfSpotsLeft =
    (showAvailability && getNumberOfSpots(spots, settings, t)) || '';
  return {
    duration,
    durationAria,
    price,
    location,
    locations,
    isBookable,
    ariaPrice,
    numberOfSpotsLeft,
    isFullyBooked: bookingPolicyDto.isFullyBooked,
  };
};
